/**
 * @fileoverview Legacy migration page shell for the system-web admin portal.
 * @module system-web/pages/migration/LegacyMigrationPage
 *
 * Responsibilities:
 * - Provide the top-level migration UI: schema manifest, department picker, dataset grid, run history.
 * - Coordinate the EligibleTenantPicker, LegacyMigrationGrid, RunHistoryPanel, and ManifestEditorTab.
 * - Orchestrate the "Start Run" confirm modal and submission through legacyMigrationService.
 * - Render DatasetTemplateGrid for the /legacy-migration/datasets route (system-level, no tenant required).
 *
 * Dependencies:
 * - lucide-react (icons)
 * - useEligibleTenants hook
 * - useLegacyMigrationGrid hook
 * - useLegacyRunHistory hook
 * - EligibleTenantPicker component
 * - LegacyMigrationGrid component
 * - RunHistoryPanel component
 * - ManifestEditorTab component
 * - DatasetTemplateGrid component (Phase 0.5B — G-078)
 * - startRun from legacyMigrationService
 *
 * Architecture:
 * - Local-first: tenant list from Dexie (useEligibleTenants liveQuery).
 * - Online-exception: grid rows from live SQL fetch (useLegacyMigrationGrid).
 * - Run history: local-first Dexie liveQuery (useLegacyRunHistory).
 * - startRun: online mutation through legacyMigrationService (writes provisional row to Dexie).
 * - DatasetTemplateGrid: local-first liveQuery from legacy_migration_dataset_templates Dexie; no tenant required.
 *
 * Notes:
 * - Phase 0: grid columns are derived from adapter metadata returned by GET /datasets.
 *   Until Phase 1, grid shows empty data with column stubs.
 * - Dataset tabs are driven by REGISTERED_ADAPTERS in the local ADAPTER_META constant.
 * - The confirm modal is a lightweight inline implementation (full version in Phase 1).
 * - /legacy-migration/datasets renders the DatasetTemplateGrid without a tenant sidebar.
 * - Full screen wraps the page header + dataset tabs + grid (not only the table).
 * - Job-complete and Grid/Run History tab enter refresh receipts/runs without leaving the page.
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowDownToLine, Play, RefreshCw, Loader2, XCircle, CheckCircle2, AlertTriangle, ChevronDown, Minimize2, Trash2, X } from 'lucide-react';
import Dexie, { liveQuery } from 'dexie';
import { getSystemDb, getSystemDexieRaw } from '@firesquirrel/local-db';
import { useEligibleTenants } from '../../hooks/useEligibleTenants';
import { useLegacyMigrationGrid } from '../../hooks/useLegacyMigrationGrid';
import { useLegacyRunHistory } from '../../hooks/useLegacyRunHistory';
import { useLegacyDbOptions } from '../../hooks/useLegacyDbOptions';
import { EligibleTenantPicker, type TenantMigrationCompleteness } from '../../components/migration/EligibleTenantPicker';
import { LegacyMigrationGrid } from '../../components/migration/LegacyMigrationGrid';
import { RunHistoryPanel } from '../../components/migration/RunHistoryPanel';
import {
  DatasetProgressStrip,
  LatestDepartmentStatusChip,
  MigrationProgressSidebar,
} from '../../components/migration/MigrationProgressSidebar';
import { DepartmentWideConfirmModal } from '../../components/migration/DepartmentWideConfirmModal';
import { ClearMigratedDataModal } from '../../components/migration/ClearMigratedDataModal';
import { ClearJobStatusCard, type ClearJobStatus } from '../../components/migration/ClearJobStatusCard';
import { useDepartmentWideRun } from '../../hooks/useDepartmentWideRun';
import { isDepartmentWideRunLive, shouldShowDatasetProgressStrip } from '../../services/departmentWideStepper';
import { ManifestEditorTab } from '../../components/migration/ManifestEditorTab';
import { SchemaManifestEditor } from '../../components/migration/SchemaManifestEditor';
import { DatasetTemplateGrid } from '../../components/migration/DatasetTemplateGrid';
import {
  startRun,
  cancelRun,
  resumeRun,
  fetchDatasets,
  fetchDepartmentWideEstimate,
  fetchAndSyncRuns,
  previewClearMigratedData,
  clearMigratedData,
  type DatasetAdapterMeta,
  type DepartmentWideEstimateRow,
  type ClearMigrationScope,
  resolveClearExecutionMode,
  type ClearMigrationTenantSummary,
} from '../../services/legacyMigrationService';
import { DocumentsStorageBanner } from '../../components/migration/DocumentsStorageBanner';
import { DEPARTMENT_WIDE_ADAPTER_KEYS, STEP_HEARTBEAT_STALE_MS, buildDepartmentMigrationCompletenessByTenant, formatRehostProgressLine, sortAdaptersForMigrationTabs } from '../../services/departmentWideStepper';
import { effectiveSelectionCount, resolveStartRunSelection, visibleSelectionCount } from '../../services/legacyMigrationSelection';
import { persistLegacyMigrationTenantId, readPersistedLegacyMigrationTenantId } from '../../services/legacyMigrationRunNav';
import { nextDepartmentRunHeartbeatExpiry } from '../../services/departmentMigrationFreshness';
import type { EligibleTenantEntry } from '../../hooks/useEligibleTenants';
import type { GridColumnDef } from '../../components/migration/LegacyMigrationGrid';
import type { BaselineRelationship } from '../../components/migration/ManifestEditorTab';

// ---------------------------------------------------------------------------
// Adapter metadata
// (Phase 0: static stubs matching the registered adapters; columns filled in Phase 1)
// ---------------------------------------------------------------------------

/**
 * @contract AdapterMeta
 *
 * Purpose:
 * - Client-side metadata about a registered dataset adapter for tab rendering.
 *
 * Fields:
 * - key: Adapter dataset key (matches server REGISTERED_ADAPTERS).
 * - displayName: Human-readable label for the tab.
 * - gridColumns: Column definitions for the grid (empty in Phase 0).
 * - primaryTable: The baseline manifest primaryTable name.
 * - childTableNames: Set of baseline childTable names.
 * - baselineRelationships: Baseline relationships from the adapter manifest.
 * - baselineTableCount: Source table count for the manifest editor.
 * - baselineCollectionCount: Target collection count for the manifest editor.
 *
 * Rules:
 * - key must match a registered adapter key on the server.
 * - gridColumns are populated in Phase 1 by the adapter.
 *
 * Sync:
 * - In Phase 1, these are hydrated from GET /api/system/legacy-migration/datasets.
 */
interface AdapterMeta {
  key: string;
  displayName: string;
  gridColumns: GridColumnDef[];
  primaryTable: string;
  childTableNames: Set<string>;
  baselineRelationships: BaselineRelationship[];
  baselineTableCount: number;
  baselineCollectionCount: number;
}

// Dataset keys hidden from the top dataset navigation tab bar and individual grids.
// Field responses and violations are child properties of Inspection Cases & Events and
// are migrated automatically as part of the Inspections suite / department-wide run.
const HIDDEN_DATASET_KEYS = new Set<string>([
  'buildingHistory',
  'inspectionFieldResponses',
  'inspectionViolations',
]);

// Phase 0: static stubs derived from adapter manifests in @firesquirrel/legacy-migration.
// Phase 1 will hydrate these from GET /api/system/legacy-migration/datasets.
const ADAPTER_META: AdapterMeta[] = sortAdaptersForMigrationTabs([
  {
    key: 'referenceLookups',
    displayName: 'Reference Lookups',
    gridColumns: [],
    primaryTable: 'lookup_main',
    childTableNames: new Set(['occ_Category', 'occ_main']),
    baselineRelationships: [],
    baselineTableCount: 3,
    baselineCollectionCount: 2,
  },
  {
    key: 'udfBlueprints',
    displayName: 'UDF Blueprints',
    gridColumns: [],
    primaryTable: 'ModuleDynamicFields',
    childTableNames: new Set(),
    baselineRelationships: [],
    baselineTableCount: 1,
    baselineCollectionCount: 1,
  },
  {
    key: 'inspectableEntities',
    displayName: 'Occupancies',
    gridColumns: [],
    primaryTable: 'occ_main',
    childTableNames: new Set(),
    baselineRelationships: [],
    baselineTableCount: 1,
    baselineCollectionCount: 1,
  },
  {
    key: 'occupancyContacts',
    displayName: 'Contacts',
    gridColumns: [],
    primaryTable: 'occ_cont',
    childTableNames: new Set(),
    baselineRelationships: [],
    baselineTableCount: 1,
    baselineCollectionCount: 2,
  },
  {
    key: 'buildingInformationalItems',
    displayName: 'Building Intel',
    gridColumns: [],
    primaryTable: 'risk_informationitem',
    childTableNames: new Set(['risk_informationalitem_att']),
    baselineRelationships: [],
    baselineTableCount: 2,
    baselineCollectionCount: 1,
  },
  {
    key: 'communicationLogs',
    displayName: 'Communication Logs',
    gridColumns: [],
    primaryTable: 'occ_callrecords',
    childTableNames: new Set(),
    baselineRelationships: [],
    baselineTableCount: 1,
    baselineCollectionCount: 1,
  },
  {
    key: 'buildingSystems',
    displayName: 'Systems',
    gridColumns: [],
    primaryTable: 'system_firealarm',
    childTableNames: new Set([
      'system_backuppower',
      'system_buildingsystems',
      'system_departmentacess',
      'system_extinguishingequipment',
      'system_extinguishing',
      'system_firepump',
    ]),
    baselineRelationships: [],
    baselineTableCount: 7,
    baselineCollectionCount: 1,
  },
  {
    key: 'masterChemicals',
    displayName: 'Master Chemicals',
    gridColumns: [],
    primaryTable: 'chem_main',
    childTableNames: new Set(),
    baselineRelationships: [],
    baselineTableCount: 1,
    baselineCollectionCount: 1,
  },
  {
    key: 'occupancyChemicals',
    displayName: 'Chemicals',
    gridColumns: [],
    primaryTable: 'occ_chem',
    childTableNames: new Set(),
    baselineRelationships: [],
    baselineTableCount: 1,
    baselineCollectionCount: 2,
  },
  {
    key: 'occupancyTanks',
    displayName: 'Tanks',
    gridColumns: [],
    primaryTable: 'occ_tank',
    childTableNames: new Set(),
    baselineRelationships: [],
    baselineTableCount: 1,
    baselineCollectionCount: 2,
  },
  {
    key: 'occupancyPermits',
    displayName: 'Permit',
    gridColumns: [],
    primaryTable: 'occ_perm',
    childTableNames: new Set(),
    baselineRelationships: [],
    baselineTableCount: 1,
    baselineCollectionCount: 1,
  },
  {
    key: 'riskProfiles',
    displayName: 'Risk Assessment',
    gridColumns: [],
    primaryTable: 'ovap_OvapMain',
    childTableNames: new Set(['ovap_AssessmentScore']),
    baselineRelationships: [],
    baselineTableCount: 2,
    baselineCollectionCount: 1,
  },
  {
    key: 'tceCompliance',
    displayName: 'TCE',
    gridColumns: [],
    primaryTable: 'TCELink_OccupancyPremise',
    childTableNames: new Set(),
    baselineRelationships: [],
    baselineTableCount: 1,
    baselineCollectionCount: 1,
  },
  {
    key: 'buildingHistory',
    displayName: 'Inspection History',
    gridColumns: [],
    primaryTable: 'occ_ins_history',
    childTableNames: new Set(),
    baselineRelationships: [],
    baselineTableCount: 1,
    baselineCollectionCount: 1,
  },
  {
    key: 'crrProfile',
    displayName: 'CRR',
    gridColumns: [],
    primaryTable: 'crr_OccupancyDetail',
    childTableNames: new Set(['crr_InspectionDetail']),
    baselineRelationships: [],
    baselineTableCount: 2,
    baselineCollectionCount: 1,
  },
  {
    key: 'crrDetector',
    displayName: 'CRR Detectors',
    gridColumns: [],
    primaryTable: 'crr_Detector',
    childTableNames: new Set(['crr_DetectorStatus']),
    baselineRelationships: [],
    baselineTableCount: 2,
    baselineCollectionCount: 1,
  },
  {
    key: 'crrDetectorEvent',
    displayName: 'CRR Detector Events',
    gridColumns: [],
    primaryTable: 'crr_DetectorEvent',
    childTableNames: new Set(['crr_DetectorEventType']),
    baselineRelationships: [],
    baselineTableCount: 2,
    baselineCollectionCount: 1,
  },
  {
    key: 'hydrantMain',
    displayName: 'Hydrants',
    gridColumns: [],
    primaryTable: 'hydrant_main',
    childTableNames: new Set([
      'hydrant_propertyhydrantlink',
      'hydrant_defects',
      'hydrant_inspectiondata',
    ]),
    baselineRelationships: [],
    baselineTableCount: 4,
    baselineCollectionCount: 1,
  },
  {
    key: 'fileMetadata',
    displayName: 'Documents',
    gridColumns: [],
    primaryTable: 'occ_main_att',
    childTableNames: new Set([
      'risk_informationalitem_att',
      'system_BackupPowerAttachment',
      'system_extinguishing_att',
      'system_AlarmAttachmentDetail',
      'system_ExtinguishingAttachmentDetail',
      'occ_tank_att',
      'occ_chem_att',
      'hydrant_main_att',
      'crr_detector_att',
      'system_AlarmAttachment',
      'system_FirePumpAttachment',
      'system_DepartmentAccessAttachment',
    ]),
    baselineRelationships: [],
    baselineTableCount: 13,
    baselineCollectionCount: 3,
  },
  {
    key: 'violationCodeSets',
    displayName: 'Violation Code Sets',
    gridColumns: [],
    primaryTable: 'Lookup_viol_codeset',
    childTableNames: new Set(['lookup_viol', 'dept_violcodesetlink']),
    baselineRelationships: [],
    baselineTableCount: 3,
    baselineCollectionCount: 2,
  },
  {
    key: 'inspectionCasesAndEvents',
    displayName: 'Inspection Cases and Events',
    gridColumns: [
      { key: 'frusa_insp_id', header: 'Inspection ID', width: 120 },
      { key: 'seriesKey', header: 'Series ID', width: 120 },
      { key: 'inspections.length', header: 'Inspections', width: 100 },
      { key: 'inspections[0].entity_name', header: 'Entity', width: 240 },
      { key: 'inspections[0].status_name', header: 'Status', width: 150 },
    ],
    primaryTable: 'ins_main',
    childTableNames: new Set(['ins_events']),
    baselineRelationships: [],
    baselineTableCount: 2,
    baselineCollectionCount: 1,
  },
  {
    key: 'inspectionFieldResponses',
    displayName: 'Inspection Field Responses',
    gridColumns: [
      { key: 'frusa_insp_id', header: 'Inspection ID', width: 120 },
      { key: 'fieldId', header: 'Field', width: 120 },
    ],
    primaryTable: 'section_field_response',
    childTableNames: new Set(['section_field_response_att']),
    baselineRelationships: [],
    baselineTableCount: 2,
    baselineCollectionCount: 1,
  },
  {
    key: 'inspectionViolations',
    displayName: 'Inspection Violations',
    gridColumns: [
      { key: 'frusa_insp_id', header: 'Inspection ID', width: 120 },
      { key: 'fieldId', header: 'Field', width: 120 },
    ],
    primaryTable: 'section_field_violation',
    childTableNames: new Set(['section_field_violation_att']),
    baselineRelationships: [],
    baselineTableCount: 2,
    baselineCollectionCount: 1,
  },
]);

/**
 * Map server adapter metadata (GET /datasets) into the page's AdapterMeta shape.
 * Replaces the static TBD_ stubs with the real manifest tables, columns, and relationships.
 */
function hydrateAdapterMeta(datasets: DatasetAdapterMeta[]): AdapterMeta[] {
  return sortAdaptersForMigrationTabs(datasets).map((d) => {
    const src = d.manifest.source;
    return {
      key: d.key,
      displayName: d.displayName,
      gridColumns: d.gridColumns.map((c) => ({
        key: c.key,
        header: c.header,
        width: c.width,
        render: c.render as GridColumnDef['render'],
      })),
      primaryTable: src.primaryTable.table,
      childTableNames: new Set(src.childTables.map((t) => t.table)),
      baselineRelationships: src.relationships.map((r) => ({
        matchKey: `${r.parent.table}.${r.parent.column} -> ${r.child.table}.${r.child.column}`,
        parent: r.parent,
        child: r.child,
        cardinality: r.cardinality,
        enforcement: r.enforcement as BaselineRelationship['enforcement'],
        orphanPolicy: r.orphanPolicy,
        orphanPolicyJustification: r.orphanPolicyJustification,
      })),
      baselineTableCount: 1 + src.childTables.length,
      baselineCollectionCount: d.manifest.target.collections.length,
    };
  });
}

// ---------------------------------------------------------------------------
// Page tab types
// ---------------------------------------------------------------------------

type PageTab = 'schema' | 'grid' | 'history' | 'manifest';
type LegacyDbName = 'SLInspectionsDB001' | 'SLInspectionsDB002';

const LEGACY_SCHEMA_DATABASES: LegacyDbName[] = ['SLInspectionsDB001', 'SLInspectionsDB002'];

// ---------------------------------------------------------------------------
// Confirm run modal
// ---------------------------------------------------------------------------

/**
 * @contract ConfirmRunModalProps
 *
 * Purpose:
 * - Props for the inline confirm-run modal.
 *
 * Fields:
 * - selectedCount: Number of selected rows.
 * - mode: 'dry-run' or 'apply'.
 * - isSubmitting: True while startRun is in-flight.
 * - error: Non-null when startRun fails.
 * - onConfirm: Callback to confirm and execute the run.
 * - onCancel: Callback to dismiss the modal.
 *
 * Rules:
 * - Confirm button disabled when isSubmitting. Cancel/Close stays enabled so a hung start cannot trap the dialog.
 * - Supports keyboard dismissal via Escape, backdrop click, and header close button.
 *
 * Sync:
 * - Not persisted to Dexie before confirmation.
 */
interface ConfirmRunModalProps {
  selectedCount: number;
  mode: 'dry-run' | 'apply';
  isSubmitting: boolean;
  error: string | null;
  unmetDeps?: string[];
  bundleDeps?: boolean;
  onBundleDepsChange?: (checked: boolean) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

function ConfirmRunModal({
  selectedCount,
  mode,
  isSubmitting,
  error,
  unmetDeps = [],
  bundleDeps = true,
  onBundleDepsChange,
  onConfirm,
  onCancel,
}: ConfirmRunModalProps) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onCancel}
          aria-label="Close dialog"
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
        <h2 className="text-lg font-semibold mb-2 pr-6">
          {mode === 'dry-run' ? 'Preview Migration' : 'Confirm Migration'}
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          {mode === 'dry-run'
            ? `Run a dry-run for ${selectedCount} selected record${selectedCount !== 1 ? 's' : ''}? No data will be written.`
            : `Migrate ${selectedCount} selected record${selectedCount !== 1 ? 's' : ''} into FireSight? This cannot be undone.`}
        </p>

        {unmetDeps && unmetDeps.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 mb-4 space-y-2">
            <div className="font-semibold flex items-center gap-1.5 text-amber-900">
              <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
              <span>Unmigrated dependencies detected</span>
            </div>
            <ul className="list-disc list-inside space-y-0.5 pl-1 text-amber-800">
              {unmetDeps.map((dep) => (
                <li key={dep}>Migrate {dep} first</li>
              ))}
            </ul>
            <label className="flex items-center gap-2 pt-1 font-medium text-amber-900 cursor-pointer">
              <input
                type="checkbox"
                checked={bundleDeps}
                onChange={(e) => onBundleDepsChange?.(e.target.checked)}
                className="rounded border-amber-400 text-orange-600 focus:ring-orange-500"
              />
              <span>Also migrate these dependencies now</span>
            </label>
            <p className="text-[11px] text-amber-700 pl-5">
              Automatically runs each dependency in topological order before migrating the selected records.
            </p>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 mb-4">
            <XCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
          >
            {isSubmitting ? 'Close' : 'Cancel'}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting || selectedCount === 0}
            className="px-4 py-2 text-sm rounded-lg text-white disabled:opacity-50"
            style={{ background: mode === 'apply' ? '#dc2626' : '#2563eb' }}
          >
            {isSubmitting ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Starting…
              </span>
            ) : mode === 'dry-run' ? (
              'Start Dry Run'
            ) : (
              'Confirm Migrate'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

/**
 * @component LegacyMigrationPage
 *
 * Purpose:
 * - System-admin page for reviewing and running legacy data migrations from Streamline.
 *
 * Props:
 * - None (route component).
 *
 * Behavior:
 * - Schema route: legacy database selector and full database SchemaManifestEditor; no department required.
 * - Dataset/run routes: EligibleTenantPicker selecting which department to migrate.
 * - Grid shows live Streamline SQL rows with compatibility scores.
 * - Run History shows past runs from Dexie.
 * - Toolbar: Start Dry Run / Start Migration buttons disabled until rows are selected.
 *
 * Data Source:
 * - local (Dexie via useEligibleTenants, useLegacyRunHistory)
 * - remote (online-exception SQL fetch via useLegacyMigrationGrid)
 *
 * Constraints:
 * - No direct apiFetch calls — all data goes through hooks and services.
 *
 * Performance:
 * - Grid uses server-side pagination (default page size 50).
 */
export default function LegacyMigrationPage() {
  const location = useLocation();
  const navigate = useNavigate();

  // Derive active top-level tab from URL path
  const pageTab = useMemo<PageTab>(() => {
    if (location.pathname.startsWith('/legacy-migration/runs')) return 'history';
    if (location.pathname.startsWith('/legacy-migration/datasets')) return 'grid';
    return 'schema'; // /legacy-migration/schema (default)
  }, [location.pathname]);

  // Tenant picker state
  const { eligible, brokenLink, hiddenCount, isLoading: tenantsLoading, error: tenantsError } = useEligibleTenants();
  const [selectedTenant, setSelectedTenant] = useState<EligibleTenantEntry | null>(null);
  const [selectedLegacyDb, setSelectedLegacyDb] = useState<LegacyDbName>('SLInspectionsDB001');

  // Physical Streamline database name per logical key for the current server environment —
  // shown alongside every "SLInspectionsDB00N" label so it's clear which environment's copy
  // is actually queried, without waiting for a live SQL fetch.
  const { physicalByLogical: legacyPhysicalByLogical } = useLegacyDbOptions();
  // Datasets tab is system-level — no tenant required. Schema tab also never requires a tenant.
  const requiresTenant = pageTab !== 'schema' && pageTab !== 'grid';

  // Dataset tab state — start from static stubs, hydrate from GET /datasets on mount.
  // HIDDEN_DATASET_KEYS is filtered out of both the static and hydrated lists.
  const [datasetMeta, setDatasetMeta] = useState<AdapterMeta[]>(
    ADAPTER_META.filter((a) => !HIDDEN_DATASET_KEYS.has(a.key)),
  );
  const [activeDatasetIdx, setActiveDatasetIdx] = useState(0);
  const activeDataset = datasetMeta[activeDatasetIdx];

  useEffect(() => {
    let cancelled = false;
    fetchDatasets()
      .then((datasets) => {
        if (!cancelled && datasets.length > 0) {
          setDatasetMeta(hydrateAdapterMeta(datasets).filter((a) => !HIDDEN_DATASET_KEYS.has(a.key)));
        }
      })
      .catch((err) => {
        // Non-fatal: fall back to static stubs; log for diagnostics.
        console.warn('[LegacyMigrationPage] fetchDatasets failed; using static adapter stubs:', err);
      });
    return () => { cancelled = true; };
  }, []);

  // Grid data (online-exception) with server-side pagination
  const {
    rows,
    receipts,
    isFetching,
    fetchError,
    refetch,
    total: gridTotal,
    page: gridPage,
    pageSize: gridPageSize,
    pageCount: gridPageCount,
    setPage: setGridPage,
    setPageSize: setGridPageSize,
    bandFilter,
    setBandFilter,
    sortBy,
    sortDir,
    setSort,
    searchQuery,
    setSearchQuery,
    columnFilters,
    setColumnFilters,
    queryJson,
    setQueryJson,
    unmetDeps,
    perTableCounts,
    datasetOutcome,
    physicalDatabase,
    appEnv,
  } = useLegacyMigrationGrid(
    selectedTenant?._id ?? null,
    activeDataset?.key ?? null,
  );

  const [bundleDeps, setBundleDeps] = useState(true);

  // Run history (local-first)
  const { run: departmentWideRun, liveJob: departmentWideLiveJob } = useDepartmentWideRun(
    selectedTenant?._id ?? null,
    selectedTenant?.legacyDeptId ?? null,
  );

  const { runs, isLoading: runsLoading, isRefreshing: runsRefreshing, error: runsError, refresh: refreshRunHistory } = useLegacyRunHistory(
    selectedTenant?._id ?? null,
    activeDataset?.key ?? null,
    selectedTenant?.legacyDeptId ?? null,
  );

  // Row selection state: supports both explicit row IDs and selectionType: 'all' with exclusions
  const [selectionType, setSelectionType] = useState<'explicit' | 'all'>('explicit');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [excludedIds, setExcludedIds] = useState<Set<string>>(new Set());

  // Migrate action dropdown state
  const [migrateDropdownOpen, setMigrateDropdownOpen] = useState(false);

  // Per-dataset sub-tab (§3.10.4): Grid | Manifest | Run History. Grid is the default.
  const [datasetTab, setDatasetTab] = useState<'grid' | 'manifest' | 'runHistory'>('grid');
  const [isGridFullscreen, setIsGridFullscreen] = useState(false);

  useEffect(() => {
    const nav = location.state as { datasetKey?: string; tenantId?: string } | null;
    const datasetKey = nav?.datasetKey;
    if (datasetKey) {
      const idx = datasetMeta.findIndex((d) => d.key === datasetKey);
      if (idx >= 0) {
        setActiveDatasetIdx(idx);
        setDatasetTab('grid');
      }
    }
    const tenantId = nav?.tenantId || readPersistedLegacyMigrationTenantId();
    if (!tenantId || eligible.length === 0) return;
    const match = eligible.find((t) => t._id === tenantId);
    if (match) {
      persistLegacyMigrationTenantId(match._id);
      setSelectedTenant(match);
    }
  }, [location.state, datasetMeta, eligible]);

  const clearGridSelection = useCallback(() => {
    setSelectionType('explicit');
    setSelectedIds(new Set());
    setExcludedIds(new Set());
    setConfirmTargetSelection(null);
    setMigrateDropdownOpen(false);
  }, []);

  // Selection belongs to the tab that is open. Switching department or dataset
  // must not leave the previous tab's rows checked or counted on Migrate.
  useEffect(() => {
    clearGridSelection();
  }, [selectedTenant?._id, activeDatasetIdx, clearGridSelection]);

  // Occupancy-DAG completeness per tenant (Partial vs Migrated). Not keyed off the active tab.
  const [completenessByTenantId, setCompletenessByTenantId] = useState<Record<string, TenantMigrationCompleteness>>({});
  const [completenessNowMs, setCompletenessNowMs] = useState(() => Date.now());
  const [completenessSnapshot, setCompletenessSnapshot] = useState<{
    receipts: Array<{ tenantId?: string; datasetKey?: string; _deleted?: boolean }>;
    runs: Parameters<typeof buildDepartmentMigrationCompletenessByTenant>[1];
  }>({ receipts: [], runs: [] });
  useEffect(() => {
    let cancelled = false;
    getSystemDb()
      .then(() => {
        if (cancelled) return;
        const raw = getSystemDexieRaw();
        if (!raw) return;
        return liveQuery(async () => {
          const liveReceipts = await raw.table('legacy_migration_receipts')
            .where('deletedFlag')
            .equals('0')
            .toArray();
          const runs = await raw.table('legacy_migration_runs').where('datasetKey').anyOf(['department-wide', ...DEPARTMENT_WIDE_ADAPTER_KEYS]).toArray();
          const seen = new Set<string>();
          const receipts = liveReceipts.flatMap((receipt) => {
            const tenantId = receipt?.tenantId;
            const datasetKey = receipt?.datasetKey;
            if (typeof tenantId !== 'string' || typeof datasetKey !== 'string') return [];
            const pair = `${tenantId}\x00${datasetKey}`;
            if (seen.has(pair)) return [];
            seen.add(pair);
            return [{ tenantId, datasetKey }];
          });
          return { receipts, runs };
        }).subscribe({
          next: (snapshot) => {
            if (cancelled) return;
            const typed = snapshot as typeof completenessSnapshot;
            setCompletenessSnapshot(typed);
            // A stopped worker emits no further Dexie write. Advance the clock
            // once when this snapshot arrives so an already-expired heartbeat
            // becomes Paused. Later ticks schedule only future expiries.
            setCompletenessNowMs(Date.now());
          },
          error: () => {},
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    const nextExpiry = nextDepartmentRunHeartbeatExpiry(completenessSnapshot.runs, completenessNowMs);
    if (nextExpiry === null) return;
    const timer = window.setTimeout(
      () => setCompletenessNowMs(Date.now()),
      Math.max(0, nextExpiry - Date.now()),
    );
    return () => window.clearTimeout(timer);
  }, [completenessSnapshot.runs, completenessNowMs]);
  useEffect(() => {
    setCompletenessByTenantId(buildDepartmentMigrationCompletenessByTenant(
      completenessSnapshot.receipts,
      completenessSnapshot.runs,
      completenessNowMs,
    ));
  }, [completenessSnapshot, completenessNowMs]);

  // Confirm modal & success feedback state
  const [confirmMode, setConfirmMode] = useState<'dry-run' | 'apply' | null>(null);
  const [departmentWideMode, setDepartmentWideMode] = useState<'dry-run' | 'apply' | null>(null);
  const [confirmTargetSelection, setConfirmTargetSelection] = useState<string[] | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [runNotification, setRunNotification] = useState<string | null>(null);
  const visibleSelectedCount = visibleSelectionCount(selectedIds, rows.map((row) => row.legacyId));
  const effectiveSelectedCount = selectionType === 'all'
    ? effectiveSelectionCount({
      selectionType,
      selectedCount: visibleSelectedCount,
      excludedCount: excludedIds.size,
      serverTotal: gridTotal,
    })
    : visibleSelectedCount;

  const handleStartRun = useCallback(async (mode: 'dry-run' | 'apply', forceAll = false) => {
    if (!selectedTenant || !activeDataset) return;
    const selection = resolveStartRunSelection({
      selectionType,
      selectedIds: Array.from(selectedIds).filter((id) => rows.some((row) => row.legacyId === id)),
      excludedIds,
      sourceTables: columnFilters.sourceTable ? [columnFilters.sourceTable] : [],
      slots: columnFilters.parentCategory ? [columnFilters.parentCategory] : [],
      band: bandFilter && bandFilter !== 'all' ? bandFilter : undefined,
      searchQuery,
      columnFilters,
      queryJson,
      forceAll,
    });
    if (selection.length === 0) return;
    setConfirmTargetSelection(selection);
    setConfirmMode(mode);
    setSubmitError(null);
    setMigrateDropdownOpen(false);
  }, [selectedTenant, activeDataset, selectedIds, excludedIds, selectionType, columnFilters, bandFilter, searchQuery, queryJson, rows]);

  const handleRefreshAll = useCallback(async () => {
    refetch();
    // Delegate to the hook's refresh() rather than calling fetchAndSyncRuns/Receipts
    // directly — refresh() explicitly re-reads Dexie into state after the sync write,
    // so the button reliably reflects the latest status instead of depending solely on
    // the liveQuery subscription re-emitting.
    await refreshRunHistory();
  }, [refetch, refreshRunHistory]);

  const handleDatasetTabChange = useCallback((next: 'grid' | 'manifest' | 'runHistory') => {
    setDatasetTab(next);
    if (next === 'grid') refetch();
    if (next === 'runHistory') void refreshRunHistory();
  }, [refetch, refreshRunHistory]);

  const handleConfirmRun = useCallback(async () => {
    if (!selectedTenant || !activeDataset || !confirmMode || isSubmitting) return;
    const mode = confirmMode;
    const selection = confirmTargetSelection && confirmTargetSelection.length > 0
      ? confirmTargetSelection
      : Array.from(selectedIds);
    const count = selection.includes('*')
      ? Math.max(0, (gridTotal || rows.length) - selection.filter((value) => value.startsWith('!')).length)
      : selection.filter((value) => !value.startsWith('@')).length;

    // Immediately dismiss the confirmation modal upon confirmation so the alert does not trap or hang
    setConfirmMode(null);
    setConfirmTargetSelection(null);
    setSubmitError(null);
    setSelectedIds(new Set());
    setExcludedIds(new Set());
    setSelectionType('explicit');

    setIsSubmitting(true);
    setRunNotification(
      mode === 'dry-run'
        ? `Starting dry run for ${count} record(s)...`
        : `Starting migration for ${count} record(s)...`
    );

    try {
      await startRun(activeDataset.key, {
        tenantId: selectedTenant._id,
        mode,
        selection,
        bundleDeps: unmetDeps.length > 0 ? bundleDeps : undefined,
        parentRunId: departmentWideRun?._id,
      });
      setRunNotification(
        mode === 'dry-run'
          ? `Dry Run completed successfully for ${count} record(s). View receipt below in Run History.`
          : `Migration is running for ${count} record(s). Progress for this dataset updates on this tab.`
      );
      void refreshRunHistory();
      refetch();
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Failed to start run';
      setRunNotification(`Failed to start migration: ${errMsg}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedTenant, activeDataset, confirmMode, isSubmitting, confirmTargetSelection, selectedIds, gridTotal, rows.length, unmetDeps.length, bundleDeps, departmentWideRun?._id, refreshRunHistory, refetch]);

  const [dwEstimate, setDwEstimate] = useState<{ available: boolean | null; error?: string; adapters: DepartmentWideEstimateRow[] } | null>(null);
  const [dwEstimateLoading, setDwEstimateLoading] = useState(false);

  useEffect(() => {
    if (!departmentWideMode || !selectedTenant?._id) {
      setDwEstimate(null);
      return;
    }
    let cancelled = false;
    setDwEstimateLoading(true);
    void fetchDepartmentWideEstimate(selectedTenant._id)
      .then((result) => {
        if (!cancelled) setDwEstimate(result);
      })
      .catch((err: unknown) => {
        if (!cancelled) setDwEstimate({
          available: false,
          error: err instanceof Error ? err.message : 'Failed to check legacy SQL',
          adapters: [],
        });
      })
      .finally(() => {
        if (!cancelled) setDwEstimateLoading(false);
      });
    return () => { cancelled = true; };
  }, [departmentWideMode, selectedTenant?._id]);

  const departmentWideAdapters = useMemo(
    () => (dwEstimate?.adapters ?? []).map((a) => ({ key: a.key, displayName: a.displayName })),
    [dwEstimate],
  );

  const handleConfirmDepartmentWide = useCallback(async (fieldResponseWindowMonths = 6) => {
    if (!selectedTenant || !departmentWideMode || isSubmitting) return;
    const mode = departmentWideMode;

    // Immediately dismiss the confirmation modal upon confirmation so the alert does not trap or hang
    setDepartmentWideMode(null);
    setSubmitError(null);

    setIsSubmitting(true);
    setRunNotification(
      mode === 'dry-run'
        ? 'Starting department-wide dry run...'
        : 'Starting department-wide migration...'
    );

    try {
      await startRun('department-wide', {
        tenantId: selectedTenant._id,
        mode,
        selection: ['*'],
        bundleDeps: true,
        fieldResponseWindowMonths,
      });
      setRunNotification(
        mode === 'dry-run'
          ? 'Department-wide dry run completed. Open Run History to inspect per-dataset steps.'
          : 'Department-wide migration started. Occupancies SQL extract can take several minutes — percent and step text update as each dataset writes.',
      );
      if (selectedTenant.legacyDeptId) {
        void fetchAndSyncRuns(selectedTenant.legacyDeptId, 'department-wide');
      }
      void refreshRunHistory();
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Failed to start department-wide run';
      setRunNotification(`Failed to start department-wide migration: ${errMsg}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedTenant, departmentWideMode, isSubmitting, refreshRunHistory]);

  const [stoppingRun, setStoppingRun] = useState(false);
  const [stoppingTabRun, setStoppingTabRun] = useState(false);
  const [resumingTabRunId, setResumingTabRunId] = useState<string | null>(null);
  const [dismissedStripRunId, setDismissedStripRunId] = useState<string | null>(null);
  const [clearModalOpen, setClearModalOpen] = useState(false);
  const [clearJob, setClearJob] = useState<ClearJobStatus | null>(null);
  const clearRunning = clearJob?.status === 'running';
  const [clearScope, setClearScope] = useState<ClearMigrationScope>('dataset');
  const [clearTenantIds, setClearTenantIds] = useState<string[]>([]);
  const clearExecutionLabel = resolveClearExecutionMode(window.location.hostname) === 'inline'
    ? 'System-API (localhost, inline)'
    : 'Worker Job (Service Bus)';
  const [clearPreviewSummaries, setClearPreviewSummaries] = useState<ClearMigrationTenantSummary[] | null>(null);
  const [clearPreviewLoading, setClearPreviewLoading] = useState(false);
  const [clearPreviewError, setClearPreviewError] = useState<string | null>(null);

  const openClearModal = useCallback((initialScope: ClearMigrationScope) => {
    setMigrateDropdownOpen(false);
    setClearScope(initialScope);
    setClearTenantIds(selectedTenant ? [selectedTenant._id] : []);
    setClearJob(null);
    setClearModalOpen(true);
  }, [selectedTenant]);

  useEffect(() => {
    if (!clearModalOpen || clearTenantIds.length === 0 || clearJob) {
      setClearPreviewSummaries(null);
      setClearPreviewError(null);
      return;
    }
    if (clearScope === 'dataset' && !activeDataset?.key) return;
    let cancelled = false;
    setClearPreviewLoading(true);
    setClearPreviewError(null);
    void previewClearMigratedData({
      tenantIds: clearTenantIds,
      scope: clearScope,
      datasetKey: clearScope === 'dataset' ? activeDataset?.key : undefined,
      cascadeChildren: true,
    })
      .then((res) => {
        if (!cancelled) setClearPreviewSummaries(res.summaries);
      })
      .catch((err) => {
        if (!cancelled) {
          setClearPreviewError(err instanceof Error ? err.message : 'Failed to preview clear counts');
        }
      })
      .finally(() => {
        if (!cancelled) setClearPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clearModalOpen, clearTenantIds, clearScope, activeDataset?.key, clearJob]);

  const handleConfirmClear = useCallback(async () => {
    if (clearTenantIds.length === 0 || clearJob?.status === 'running') return;
    if (clearScope === 'dataset' && !activeDataset?.key) return;
    setClearModalOpen(false);
    const targetLabel = clearScope === 'department-all'
      ? 'all tabs'
      : (activeDataset?.displayName ?? activeDataset?.key ?? 'this tab');
    setClearJob({
      status: 'running',
      scope: clearScope,
      targetLabel,
      datasetKey: clearScope === 'dataset' ? activeDataset?.key : undefined,
      departmentName: selectedTenant?.name ?? 'Selected department',
      startedAt: Date.now(),
    });
    try {
      const outcome = await clearMigratedData(
        {
          tenantIds: clearTenantIds,
          scope: clearScope,
          datasetKey: clearScope === 'dataset' ? activeDataset?.key : undefined,
          cascadeChildren: true,
        },
        (info) => setClearJob((prev) => (prev
          ? { ...prev, jobId: info.jobId ?? prev.jobId, executionMode: info.executionMode, progress: info.progress ?? prev.progress }
          : prev)),
      );
      setClearJob((prev) => (prev
        ? { ...prev, status: 'completed', finishedAt: Date.now(), executionMode: outcome.executionMode, summaries: outcome.summaries }
        : prev));
      refetch();
      void refreshRunHistory();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to clear migrated data';
      setClearJob((prev) => (prev ? { ...prev, status: 'failed', finishedAt: Date.now(), error: message } : prev));
    }
  }, [clearTenantIds, clearScope, activeDataset?.key, activeDataset?.displayName, selectedTenant?.name, clearJob?.status, refetch, refreshRunHistory]);
  useEffect(() => {
    if (departmentWideRun?.status === 'cancelled' || departmentWideRun?.status === 'failed' || departmentWideRun?.status === 'completed') {
      setStoppingRun(false);
    }
  }, [departmentWideRun?.status]);
  const handleStopDepartmentWide = useCallback(async () => {
    if (!departmentWideRun?._id) return;
    setStoppingRun(true);
    try {
      await cancelRun(departmentWideRun._id);
      setRunNotification('Stop requested. The current SQL query may finish before remaining datasets are skipped.');
      if (selectedTenant?.legacyDeptId) {
        void fetchAndSyncRuns(selectedTenant.legacyDeptId, 'department-wide');
      }
      void refreshRunHistory();
    } catch (err) {
      setStoppingRun(false);
      setSubmitError(err instanceof Error ? err.message : 'Failed to stop migration');
    }
  }, [departmentWideRun?._id, refreshRunHistory, selectedTenant?.legacyDeptId]);

  const handleResumeDepartmentWide = useCallback(async () => {
    if (!departmentWideRun?._id) return;
    setSubmitError(null);
    try {
      await resumeRun(departmentWideRun._id);
      setRunNotification('Resume started. Completed datasets are skipped; remaining occupancy datasets continue from the breakpoint.');
      if (selectedTenant?.legacyDeptId) {
        void fetchAndSyncRuns(selectedTenant.legacyDeptId, 'department-wide');
      }
      void refreshRunHistory();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to resume migration';
      setSubmitError(message);
      setRunNotification(message);
    }
  }, [departmentWideRun?._id, refreshRunHistory, selectedTenant?.legacyDeptId]);

  const hasSelection = effectiveSelectedCount > 0;
  const unmetDepLabels = useMemo(
    () => unmetDeps.map((key) => datasetMeta.find((adapter) => adapter.key === key)?.displayName ?? key),
    [unmetDeps, datasetMeta],
  );
  // A stale heartbeat stays inside this sidebar so Resume remains visible. The sidebar
  // itself marks the run paused; hiding it would also hide the finished-run chip.
  const departmentWideLive = isDepartmentWideRunLive(departmentWideRun?.status);
  const liveTabRun = useMemo(
    () => runs.find((row) =>
      row.datasetKey === activeDataset?.key
      && (row.status === 'running' || row.status === 'pending' || row.rehostStatus === 'running'),
    ) ?? null,
    [runs, activeDataset?.key],
  );
  const latestTabApplyRun = useMemo(
    () => runs.find((row) => row.datasetKey === activeDataset?.key && row.mode === 'apply') ?? null,
    [runs, activeDataset?.key],
  );
  const resumableTabRun = useMemo(
    () => (latestTabApplyRun && (latestTabApplyRun.status === 'cancelled' || latestTabApplyRun.status === 'failed')
      ? latestTabApplyRun
      : null),
    [latestTabApplyRun],
  );
  // Dismissal hides only the terminal banner. A live run (including one just resumed) always shows its progress and Stop.
  const stripTabRun = liveTabRun
    ?? (resumableTabRun && resumableTabRun._id !== dismissedStripRunId ? resumableTabRun : null);
  const showDatasetStrip = shouldShowDatasetProgressStrip({
    departmentWideLive,
    hasStripRun: Boolean(stripTabRun),
    hasLiveTabRun: Boolean(liveTabRun),
  });
  useEffect(() => {
    if (!liveTabRun || liveTabRun.status === 'completed' || liveTabRun.status === 'failed' || liveTabRun.status === 'cancelled') {
      setStoppingTabRun(false);
    }
  }, [liveTabRun]);

  const prevLiveTabStatusRef = useRef<string | undefined>(undefined);
  const prevDepartmentWideStatusRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const tabStatus = liveTabRun?.status;
    const prevTab = prevLiveTabStatusRef.current;
    prevLiveTabStatusRef.current = tabStatus;
    const tabJustFinished = prevTab === 'running' || prevTab === 'pending';
    // liveTabRun is only set while pending/running — on complete the memo returns null.
    const tabNowTerminal = !tabStatus || tabStatus === 'completed' || tabStatus === 'failed' || tabStatus === 'cancelled';

    const dwStatus = departmentWideRun?.status;
    const prevDw = prevDepartmentWideStatusRef.current;
    prevDepartmentWideStatusRef.current = dwStatus;
    const dwJustFinished = isDepartmentWideRunLive(prevDw);
    const dwNowTerminal = dwStatus === 'completed' || dwStatus === 'failed' || dwStatus === 'cancelled';

    if ((tabJustFinished && tabNowTerminal) || (dwJustFinished && dwNowTerminal)) {
      void handleRefreshAll();
    }
  }, [liveTabRun?.status, departmentWideRun?.status, handleRefreshAll]);

  useEffect(() => {
    if (!isGridFullscreen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsGridFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isGridFullscreen]);
  const handleStopTabRun = useCallback(async () => {
    if (!liveTabRun?._id) return;
    setStoppingTabRun(true);
    try {
      await cancelRun(liveTabRun._id);
      setRunNotification('Stop requested for this dataset. You can click Resume remaining to continue from the checkpoint.');
      void refreshRunHistory();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to stop dataset migration');
    } finally {
      setStoppingTabRun(false);
    }
  }, [liveTabRun?._id, refreshRunHistory]);
  const handleResumeTabRun = useCallback(async (targetRunId?: string) => {
    const idToResume = targetRunId ?? stripTabRun?._id;
    if (!idToResume) return;
    setResumingTabRunId(idToResume);
    setSubmitError(null);
    try {
      await resumeRun(idToResume);
      setDismissedStripRunId((current) => (current === idToResume ? null : current));
      setRunNotification('Resume started. Already-migrated records are skipped and remaining records will continue.');
      if (selectedTenant?.legacyDeptId && activeDataset?.key) {
        void fetchAndSyncRuns(selectedTenant.legacyDeptId, activeDataset.key);
      }
      void refreshRunHistory();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to resume dataset migration');
    } finally {
      setResumingTabRunId(null);
    }
  }, [stripTabRun?._id, selectedTenant?.legacyDeptId, activeDataset?.key, refreshRunHistory]);
  const openDepartmentReport = departmentWideRun?._id
    ? () => navigate(`/legacy-migration/department-runs/${departmentWideRun._id}`)
    : undefined;

  return (
    <div className="flex h-full min-h-0" style={{ fontFamily: 'inherit' }}>

      {requiresTenant && (
        <aside
          className="w-64 shrink-0 border-r overflow-y-auto flex flex-col"
          style={{ borderColor: 'rgba(0,0,0,0.08)' }}
        >
          <div className="px-4 py-3 border-b" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
            <h2 className="text-sm font-semibold">Departments</h2>
            <p className="text-xs opacity-50 mt-0.5">Select a department to migrate</p>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            <EligibleTenantPicker
              eligible={eligible}
              brokenLink={brokenLink}
              hiddenCount={hiddenCount}
              isLoading={tenantsLoading}
              error={tenantsError}
              selectedId={selectedTenant?._id ?? null}
              onSelect={(tenant) => {
                persistLegacyMigrationTenantId(tenant._id);
                setSelectedTenant(tenant);
              }}
              physicalByLogical={legacyPhysicalByLogical}
              completenessByTenantId={completenessByTenantId}
              appEnv={appEnv}
            />
          </div>
        </aside>
      )}

      {/* Main content — full remaining width (no right-side progress pane) */}
      <div className={isGridFullscreen
        ? 'fixed inset-0 z-[60] flex flex-col overflow-hidden bg-white'
        : 'flex-1 min-w-0 flex flex-col overflow-hidden'}>
        {departmentWideLive && (
          <MigrationProgressSidebar
            run={departmentWideRun}
            liveJob={departmentWideLiveJob}
            onStop={handleStopDepartmentWide}
            onResume={handleResumeDepartmentWide}
            onViewReport={openDepartmentReport}
            stopping={stoppingRun}
          />
        )}
        {showDatasetStrip && stripTabRun && pageTab !== 'history' && (
          <DatasetProgressStrip
            run={stripTabRun}
            datasetLabel={activeDataset?.displayName ?? 'dataset'}
            onStop={liveTabRun ? handleStopTabRun : undefined}
            onResume={() => { if (stripTabRun) void handleResumeTabRun(stripTabRun._id); }}
            onDismiss={resumableTabRun ? () => setDismissedStripRunId(resumableTabRun._id) : undefined}
            onViewDepartmentReport={openDepartmentReport}
            stopping={stoppingTabRun}
            resuming={resumingTabRunId === stripTabRun._id}
          />
        )}
        {!departmentWideLive && !stripTabRun && (
          <LatestDepartmentStatusChip
            run={departmentWideRun}
            onViewReport={openDepartmentReport}
            onResume={handleResumeDepartmentWide}
          />
        )}
        {submitError && !confirmMode && !departmentWideMode && (
          <div className="mx-4 mt-3 px-4 py-2.5 bg-red-50 text-red-800 border border-red-200 rounded-lg text-sm flex items-center justify-between shrink-0">
            <span>{submitError}</span>
            <button type="button" onClick={() => setSubmitError(null)} className="text-xs font-semibold hover:underline ml-4">
              Dismiss
            </button>
          </div>
        )}

        {/* Header */}
        <div className="px-6 py-4 border-b shrink-0" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold">Legacy Migration</h1>
              {pageTab === 'schema' ? (
                <p className="text-sm opacity-60 mt-0.5">
                  {selectedLegacyDb}
                  {legacyPhysicalByLogical[selectedLegacyDb] && (
                    <span className="font-mono text-blue-600"> → {legacyPhysicalByLogical[selectedLegacyDb]}</span>
                  )}
                  {' '}· full legacy database schema
                </p>
              ) : selectedTenant ? (
                <p className="text-sm opacity-60 mt-0.5 flex items-center gap-2 flex-wrap">
                  <span>
                    {selectedTenant.name} · {selectedTenant.legacyDb}
                    {legacyPhysicalByLogical[selectedTenant.legacyDb] && (
                      <span className="font-mono text-blue-600"> → {legacyPhysicalByLogical[selectedTenant.legacyDb]}</span>
                    )}
                    {' '}· Dept {selectedTenant.legacyDeptId}
                    {appEnv && (
                      <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold tracking-wider uppercase bg-gray-100 text-gray-700 border border-gray-200">
                        {appEnv.toLowerCase() === 'prod' || appEnv.toLowerCase() === 'production'
                          ? 'Main'
                          : appEnv.toLowerCase() === 'stage' || appEnv.toLowerCase() === 'staging'
                          ? 'Stage'
                          : 'Dev'}
                      </span>
                    )}
                  </span>
                  {physicalDatabase && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono font-medium bg-blue-50 text-blue-700 border border-blue-200"
                      title={`Live SQL queries for this dataset actually connect to '${physicalDatabase}' on the shared Streamline SQL Server${appEnv ? ` (server environment: ${appEnv})` : ''}.`}
                    >
                      Connected: {physicalDatabase}
                    </span>
                  )}
                </p>
              ) : (
                <p className="text-sm opacity-40 mt-0.5">Select a department to begin</p>
              )}
            </div>

            <div className="flex items-center gap-2">
            {isGridFullscreen && (
              <button
                type="button"
                onClick={() => setIsGridFullscreen(false)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50 transition-colors"
                title="Exit full screen (Esc)"
              >
                <Minimize2 className="h-3.5 w-3.5" />
                Exit Full Screen
              </button>
            )}
            {pageTab === 'schema' && (
              <>
                <label className="text-xs font-medium text-gray-500" htmlFor="legacy-schema-db">
                  Legacy DB
                </label>
                <select
                  id="legacy-schema-db"
                  value={selectedLegacyDb}
                  onChange={(event) => setSelectedLegacyDb(event.target.value as LegacyDbName)}
                  className="px-3 py-1.5 text-sm border rounded-lg bg-white"
                >
                  {LEGACY_SCHEMA_DATABASES.map((dbName) => (
                    <option key={dbName} value={dbName}>{dbName}</option>
                  ))}
                </select>
              </>
            )}

            {selectedTenant && pageTab === 'history' && datasetTab === 'runHistory' && (
              <>
                <button
                  onClick={handleRefreshAll}
                  disabled={runsRefreshing || isFetching}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  title="Refresh runs and receipts"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${runsRefreshing || isFetching ? 'animate-spin text-orange-600' : ''}`} />
                  <span>{runsRefreshing || isFetching ? 'Refreshing History…' : 'Refresh History'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => openClearModal('department-all')}
                  disabled={clearRunning || departmentWideLive || Boolean(liveTabRun)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-red-200 text-red-700 bg-red-50/50 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                  title="Clear all migrated data for the selected department (every tab)"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  <span>Clear Department Data</span>
                </button>
              </>
            )}

            {selectedTenant && pageTab === 'history' && datasetTab === 'grid' && (
              <>
                <button
                  onClick={refetch}
                  disabled={isFetching}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  title="Refresh grid"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
                  Refresh
                </button>

                <button
                  type="button"
                  onClick={() => openClearModal('department-all')}
                  disabled={isFetching || clearRunning || departmentWideLive || Boolean(liveTabRun)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-red-200 text-red-700 bg-red-50/50 hover:bg-red-50 rounded-lg disabled:opacity-50 transition-colors"
                  title="Clear all migrated data for the selected department (every tab)"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Clear Department Data
                </button>

                <button
                  onClick={() => handleStartRun('dry-run')}
                  disabled={!hasSelection || isFetching || isSubmitting}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  <Play className="h-3.5 w-3.5" />
                  Dry Run ({effectiveSelectedCount})
                </button>

                {/* Split Dropdown: Migrate (Selected) / Migrate All */}
                <div className="relative inline-flex rounded-lg shadow-xs">
                  <button
                    type="button"
                    onClick={() => handleStartRun('apply')}
                    disabled={!hasSelection || isFetching || isSubmitting}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-white bg-orange-600 hover:bg-orange-700 rounded-l-lg disabled:opacity-50 transition-colors"
                  >
                    <ArrowDownToLine className="h-3.5 w-3.5" />
                    Migrate ({effectiveSelectedCount})
                  </button>
                  <button
                    type="button"
                    onClick={() => setMigrateDropdownOpen((prev) => !prev)}
                    disabled={isFetching || isSubmitting}
                    className="inline-flex items-center px-2 py-1.5 text-sm text-white bg-orange-600 hover:bg-orange-700 border-l border-orange-500 rounded-r-lg disabled:opacity-50 transition-colors"
                    title="More migration options"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>

                  {migrateDropdownOpen && (
                    <div className="absolute right-0 top-full mt-1 w-56 rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 z-30">
                      <button
                        type="button"
                        onClick={() => handleStartRun('apply')}
                        disabled={!hasSelection || isSubmitting}
                        className="w-full text-left px-4 py-2 text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-50 flex flex-col"
                      >
                        <span className="font-semibold">Migrate Selected ({effectiveSelectedCount})</span>
                        <span className="text-[10px] text-gray-500">Apply selected rows only</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleStartRun('apply', true)}
                        disabled={isSubmitting}
                        className="w-full text-left px-4 py-2 text-xs text-gray-700 hover:bg-gray-100 border-t border-gray-100 disabled:opacity-50 flex flex-col"
                      >
                        <span className="font-semibold text-orange-600">Migrate All Records</span>
                        <span className="text-[10px] text-gray-500">
                          {gridTotal > 0 ? `Migrate all ${gridTotal} records for this dataset` : 'Migrate entire dataset for department'}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => { setMigrateDropdownOpen(false); setDepartmentWideMode('apply'); setSubmitError(null); }}
                        disabled={isSubmitting}
                        className="w-full text-left px-4 py-2 text-xs text-gray-700 hover:bg-gray-100 border-t border-gray-100 disabled:opacity-50 flex flex-col"
                      >
                        <span className="font-semibold text-orange-700">Migrate Entire Department</span>
                        <span className="text-[10px] text-gray-500">All occupancy datasets in order (hydrants before files)</span>
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
            </div>
          </div>

          {/* Dataset tabs */}
          {selectedTenant && pageTab === 'history' && (
            <div className="flex gap-0 mt-3 border-b -mb-4 overflow-x-auto max-w-full" style={{ borderColor: 'rgba(0,0,0,0.08)', scrollbarWidth: 'thin' }}>
              {datasetMeta.map((adapter, idx) => (
                <button
                  key={adapter.key}
                  onClick={() => { setActiveDatasetIdx(idx); clearGridSelection(); }}
                  className="px-4 py-2 text-sm font-medium -mb-px transition-colors shrink-0 whitespace-nowrap"
                  style={{
                    borderBottom: activeDatasetIdx === idx ? '2px solid #ea580c' : '2px solid transparent',
                    color: activeDatasetIdx === idx ? '#ea580c' : undefined,
                    opacity: activeDatasetIdx !== idx ? 0.6 : 1,
                  }}
                >
                  {adapter.displayName}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* No tenant selected */}
        {!selectedTenant && requiresTenant && (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 opacity-40">
            <ArrowDownToLine className="h-12 w-12" />
            <p className="text-sm">Select a department from the left panel to begin migration.</p>
          </div>
        )}

        {pageTab === 'schema' && (
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            <div className="flex-1 min-h-0 flex flex-col overflow-hidden px-6 pb-6 pt-4">
              <div className="mb-3">
                <h2 className="text-sm font-semibold">Schema Manifest</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  All tables from {selectedLegacyDb}. Disable irrelevant tables, then declare the FK
                  relationships the legacy DB doesn't enforce. This shaped manifest is the raw material for
                  building migration datasets.
                </p>
              </div>
              <SchemaManifestEditor legacyDb={selectedLegacyDb} />
            </div>
          </div>
        )}

        {/* Datasets tab — system-level, no tenant required (§3.11.8) */}
        {pageTab === 'grid' && (
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            <DatasetTemplateGrid />
          </div>
        )}

        {/* Dataset workspace — tenant-scoped [Grid] [Manifest] [Run History] tabs (§3.10.4) */}
        {selectedTenant && activeDataset && pageTab === 'history' && (
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            {runNotification && !stripTabRun && (
              <div className={`mx-6 mt-3 px-4 py-2.5 rounded-lg text-sm flex items-center justify-between shrink-0 ${
                runNotification.startsWith('Failed')
                  ? 'bg-red-50 text-red-800 border border-red-200'
                  : runNotification.startsWith('Starting')
                    ? 'bg-orange-50 text-orange-900 border border-orange-200'
                    : 'bg-green-50 text-green-800 border border-green-200'
              }`}>
                <div className="flex items-center gap-2">
                  {runNotification.startsWith('Failed') ? (
                    <XCircle className="h-4 w-4 text-red-600 shrink-0" />
                  ) : runNotification.startsWith('Starting') ? (
                    <Loader2 className="h-4 w-4 text-orange-600 animate-spin shrink-0" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                  )}
                  <span>{runNotification}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setRunNotification(null)}
                  className={`text-xs font-semibold hover:underline ml-4 ${
                    runNotification.startsWith('Failed') ? 'text-red-900' : 'text-green-900'
                  }`}
                >
                  Dismiss
                </button>
              </div>
            )}

            {clearJob && (clearJob.scope === 'department-all' || clearJob.datasetKey === activeDataset.key) && (
              <div className="mx-6 mt-3 shrink-0">
                <ClearJobStatusCard
                  job={clearJob}
                  datasetLabels={Object.fromEntries(datasetMeta.map((d) => [d.key, d.displayName]))}
                  onDismiss={() => setClearJob(null)}
                />
              </div>
            )}

            {showDatasetStrip && stripTabRun && (
              <DatasetProgressStrip
                run={stripTabRun}
                datasetLabel={activeDataset?.displayName ?? 'dataset'}
                onStop={liveTabRun ? handleStopTabRun : undefined}
                onResume={() => { if (stripTabRun) void handleResumeTabRun(stripTabRun._id); }}
                onDismiss={resumableTabRun ? () => setDismissedStripRunId(resumableTabRun._id) : undefined}
                onViewDepartmentReport={openDepartmentReport}
                stopping={stoppingTabRun}
                resuming={resumingTabRunId === stripTabRun._id}
              />
            )}

            {/* Sub-tab bar */}
            <div
              className="flex gap-4 px-6 pt-3 border-b shrink-0"
              style={{ borderColor: 'rgba(0,0,0,0.08)' }}
            >
              {([
                { key: 'grid', label: 'Grid' },
                { key: 'manifest', label: 'Manifest' },
                { key: 'runHistory', label: 'Run History' },
              ] as const).map((t) => (
                <button
                  key={t.key}
                  onClick={() => handleDatasetTabChange(t.key)}
                  className="pb-2 text-sm font-medium -mb-px transition-colors"
                  style={{
                    borderBottom: datasetTab === t.key ? '2px solid #ea580c' : '2px solid transparent',
                    color: datasetTab === t.key ? '#ea580c' : undefined,
                    opacity: datasetTab === t.key ? 1 : 0.6,
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Grid tab — scored live SQL rows with Dry-Run / Migrate selection */}
            {datasetTab === 'grid' && (
              <div className="flex-1 min-h-0 flex flex-col px-6 pb-6 pt-3">
                {unmetDepLabels.length > 0 && (
                  <div className="mb-3 bg-amber-50 border border-amber-200 rounded-xl p-3 px-4 flex items-center justify-between text-xs text-amber-800">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
                      <div>
                        <span className="font-semibold text-amber-900">Migrate required datasets first: </span>
                        <span>{unmetDepLabels.join(' · ')}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setBundleDeps(true);
                        void handleStartRun('apply', effectiveSelectedCount === 0);
                      }}
                      disabled={isSubmitting}
                      className="shrink-0 px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded text-xs transition-colors disabled:opacity-50"
                    >
                      Migrate Dependencies Now
                    </button>
                  </div>
                )}
                {activeDataset.key === 'fileMetadata' && <DocumentsStorageBanner />}
                {activeDataset.key === 'fileMetadata' && (liveTabRun?.rehostCounts || departmentWideRun?.rehostCounts) && (
                  <p className="mb-3 text-xs text-gray-700">
                    {formatRehostProgressLine({
                      rehostStatus: liveTabRun?.rehostStatus ?? departmentWideRun?.rehostStatus,
                      rehostCounts: liveTabRun?.rehostCounts ?? departmentWideRun?.rehostCounts,
                    })}
                  </p>
                )}
                {activeDataset.key === 'fileMetadata' && Object.keys(perTableCounts).length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-1.5 text-[11px]" aria-label="Document counts by source table">
                    {Object.entries(perTableCounts).sort(([a], [b]) => a.localeCompare(b)).map(([table, count]) => (
                      <span key={table} className="rounded border border-gray-200 bg-gray-50 px-2 py-1 text-gray-700">
                        {table}: {count}
                      </span>
                    ))}
                    <span className="rounded border border-orange-200 bg-orange-50 px-2 py-1 font-semibold text-orange-800">
                      Total: {Object.values(perTableCounts).reduce((sum, count) => sum + count, 0)}
                    </span>
                  </div>
                )}
                <LegacyMigrationGrid
                  datasetKey={activeDataset.key}
                  toolbarActions={
                    <button
                      type="button"
                      onClick={() => openClearModal('dataset')}
                      disabled={isFetching || clearRunning || departmentWideLive || Boolean(liveTabRun)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 border border-red-200 text-red-700 bg-red-50/50 hover:bg-red-50 rounded-lg text-sm font-medium shadow-xs disabled:opacity-50 transition-colors"
                      title="Clear this tab's migrated data (dependent tabs are listed before you confirm)"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span>Remove Data</span>
                    </button>
                  }
                  rows={rows}
                  columns={activeDataset.gridColumns}
                  isFetching={isFetching}
                  fetchError={fetchError}
                  refetch={refetch}
                  selectedIds={selectedIds}
                  onSelectionChange={setSelectedIds}
                  selectionType={selectionType}
                  onSelectionTypeChange={setSelectionType}
                  excludedIds={excludedIds}
                  onExcludedIdsChange={setExcludedIds}
                  total={gridTotal}
                  page={gridPage}
                  pageSize={gridPageSize}
                  pageCount={gridPageCount}
                  onPageChange={setGridPage}
                  onPageSizeChange={setGridPageSize}
                  bandFilter={bandFilter}
                  onBandFilterChange={setBandFilter}
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSortChange={setSort}
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  columnFilters={columnFilters}
                  onColumnFiltersChange={setColumnFilters}
                  queryJson={queryJson}
                  onQueryJsonChange={setQueryJson}
                  datasetOutcome={datasetOutcome}
                  isFullscreen={isGridFullscreen}
                  onToggleFullscreen={() => setIsGridFullscreen((current) => !current)}
                />
              </div>
            )}

            {/* Manifest tab */}
            {datasetTab === 'manifest' && (
              <div className="flex-1 overflow-y-auto px-6 pb-6 pt-3 max-w-3xl">
                <ManifestEditorTab
                  datasetKey={activeDataset.key}
                  legacyDb={selectedTenant?.legacyDb ?? null}
                  primaryTable={activeDataset.primaryTable}
                  childTableNames={activeDataset.childTableNames}
                  baselineRelationships={activeDataset.baselineRelationships}
                  baselineTableCount={activeDataset.baselineTableCount}
                  baselineCollectionCount={activeDataset.baselineCollectionCount}
                />
              </div>
            )}

            {/* Run History tab */}
            {datasetTab === 'runHistory' && (
              <div className="flex-1 overflow-y-auto px-6 pb-6 pt-2">
                <RunHistoryPanel
                  runs={runs}
                  isLoading={runsLoading}
                  isRefreshing={runsRefreshing}
                  error={runsError}
                  onRefresh={handleRefreshAll}
                  onResumeRun={(id) => { void handleResumeTabRun(id); }}
                  resumingRunId={resumingTabRunId}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirm run modal */}
      {confirmMode && (
        <ConfirmRunModal
          selectedCount={confirmTargetSelection?.includes('*') ? (gridTotal || rows.length) : effectiveSelectedCount}
          mode={confirmMode}
          isSubmitting={isSubmitting}
          error={submitError}
          unmetDeps={unmetDepLabels}
          bundleDeps={bundleDeps}
          onBundleDepsChange={setBundleDeps}
          onConfirm={handleConfirmRun}
          onCancel={() => {
            setConfirmMode(null);
            setConfirmTargetSelection(null);
            setSubmitError(null);
          }}
        />
      )}
      {departmentWideMode && (
        <DepartmentWideConfirmModal
          mode={departmentWideMode}
          adapters={departmentWideAdapters}
          estimateAvailable={dwEstimate?.available}
          estimateError={dwEstimate?.error}
          estimateLoading={dwEstimateLoading}
          isSubmitting={isSubmitting}
          error={submitError}
          onConfirm={handleConfirmDepartmentWide}
          onCancel={() => {
            setDepartmentWideMode(null);
            setSubmitError(null);
          }}
        />
      )}
      {clearModalOpen && activeDataset && selectedTenant && (
        <ClearMigratedDataModal
          scope={clearScope}
          departmentName={selectedTenant.name}
          legacyDeptId={selectedTenant.legacyDeptId != null ? String(selectedTenant.legacyDeptId) : undefined}
          activeDatasetKey={activeDataset.key}
          activeDatasetDisplayName={activeDataset.displayName}
          datasetLabels={Object.fromEntries(datasetMeta.map((d) => [d.key, d.displayName]))}
          executionLabel={clearExecutionLabel}
          previewSummaries={clearPreviewSummaries}
          isPreviewLoading={clearPreviewLoading}
          previewError={clearPreviewError}
          onConfirm={handleConfirmClear}
          onCancel={() => setClearModalOpen(false)}
        />
      )}
    </div>
  );
}
